import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from "../../@/components/ui/card";
import { Button } from "../../@/components/ui/button";
import { MoreVertical, HelpCircle, Code } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../@/components/ui/dropdown-menu";
import { AddProjectDialog } from "../forms/addProject";
import { EditTopicDialog } from "../forms/editTopic";
import { EditLessonDialog } from "../forms/editLesson";
import { EmptyState } from "@/components/emptyState";
import { Spinner } from "@/components/spinner";
import { AddLessonDialog } from "@/forms/addLesson";
import { BookOpenIcon } from "lucide-react";
import { EditQuizDialog } from "../Depracated/editQuiz";
import { EditProjectDialog } from "../forms/editProject";
import DeleteTopic from "@/forms/deleteTopic";
import DeleteLesson from "@/forms/deleteLesson";
import DeleteQuiz from "@/forms/deleteQuiz";
import DeleteProject from "@/forms/deleteProject";
import { AddQuestionToQuizDialog } from "../Depracated/addQuestionsToQuiz";
import { UnifiedQuizDialog } from "../forms/unifiedQuiz";
import { UnifiedEditQuizDialog } from "../forms/unifiedEditQuiz";

export default function TopicCard({ topic, refetchTopics, course }) {
  const [lessons, setLessons] = useState(topic.lessons || []);
  const [quizzes, setQuizzes] = useState(topic.quizzes || []);
  const [projects, setProjects] = useState(topic.projects || []);

  useEffect(() => {
    setLessons(topic.lessons || []);
  }, [topic.lessons, topic.id]);

  useEffect(() => {
    setQuizzes(topic.quizzes || []);
  }, [topic.quizzes, topic.id]);

  useEffect(() => {
    setProjects(
      Array.isArray(topic.projects)
        ? topic.projects
        : topic.projects
          ? [topic.projects]
          : [],
    );
  }, [topic.projects, topic.id]);

  const handleLessonAdded = (newLesson) => {
    setLessons((prev) => {
      const exists = prev.some((lesson) => lesson.id === newLesson.id);
      if (exists) return prev;
      const combined = [...prev, newLesson];
      return combined;
    });
  };

  const handleQuizAdded = (newQuiz) => {
    console.log("New quiz being added:", newQuiz);
    setQuizzes((prev) => {
      const exists = prev.some((quiz) => quiz.id === newQuiz.id);
      if (exists) return prev;
      const combined = [...prev, newQuiz];
      return combined;
    });
  };

  const handleProjectAdded = (newProject) => {
    setProjects((prev) => {
      const exists = prev.some((project) => project.id === newProject.id);
      if (exists) return prev;
      return [...prev, newProject];
    });
  };

  // Function to render lesson dropdown
  const renderLessonDropdown = (item) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="">
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="z-50">
        <DropdownMenuItem asChild>
          <EditLessonDialog
            lessonId={item.id}
            courseSlug={course?.slug || "default-slug"}
            topicId={topic.id}
            topicOrder={topic.order}
            topicTitle={modifiedTopicName}
            initialData={{
              title: item.title,
              order: item.order,
              videoUrl: item.video_url || "",
              markdownUrl: item.markdownUrl || item.r2_public_url || item.content || "",
              content: item.content || "",
            }}
            triggerButtonProps={{
              variant: "ghost",
              size: "sm",
              className: "w-full justify-start",
            }}
            onLessonAdded={handleLessonAdded}
            refetchTopics={refetchTopics}
          />
        </DropdownMenuItem>
        {/* <DropdownMenuItem>Preview</DropdownMenuItem> */}
        <DropdownMenuItem asChild>
          <DeleteLesson
            lessonId={item.id}
            lessonTitle={item.title}
            onDeleted={refetchTopics}
            triggerButtonProps={{
              variant: "ghost",
              size: "sm",
              className: "w-full justify-start text-red hover:text-blue",
            }}
          />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Function to render quiz drodown
  const renderQuizDropdown = (item) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="">
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="border border-(--neutral-300)">
        <DropdownMenuItem asChild>
          <UnifiedEditQuizDialog
            quiz={item}
            // initialData={item}
            triggerButtonProps={{
              variant: "ghost",
              size: "sm",
              className: "w-full justify-start",
            }}
            onQuizAdded={handleQuizAdded}
            refetchTopics={refetchTopics}
            courseTitle={course?.title || "default-course"}
            courseSlug={course?.slug || "default-slug"}
          >
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start h-auto p-2 font-normal"
            >
              Edit Quiz
            </Button>
          </UnifiedEditQuizDialog>
        </DropdownMenuItem>
        {/* <DropdownMenuItem>Preview</DropdownMenuItem> */}
        <DropdownMenuItem asChild>
          <AddQuestionToQuizDialog
            quiz={item}
            onQuestionsAdded={() => {
              if (refetchTopics) refetchTopics();
            }}
            refetchTopics={refetchTopics}
          >
            {/* <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start h-auto p-2 font-normal"
            >
              Add Questions
            </Button> */}
          </AddQuestionToQuizDialog>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <DeleteQuiz
            quizId={item.id}
            quizTitle={item.title}
            onDeleted={refetchTopics}
            triggerButtonProps={{
              variant: "ghost",
              size: "sm",
              className: "w-full justify-start text-red hover:text-blue",
            }}
          />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Function to render project dropdown
  const renderProjectDropdown = (item) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="">
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="border border-(--neutral-300)">
        <DropdownMenuItem asChild>
          <EditProjectDialog
            projectId={item.id}
            initialData={item}
            triggerButtonProps={{
              variant: "ghost",
              size: "sm",
              className: "w-full justify-start",
            }}
            onProjectAdded={handleProjectAdded}
            refetchTopics={refetchTopics}
          />
        </DropdownMenuItem>
        {/* <DropdownMenuItem>Preview</DropdownMenuItem> */}
        <DropdownMenuItem asChild>
          <DeleteProject
            projectId={item.id}
            projectTitle={item.title}
            onDeleted={refetchTopics}
            triggerButtonProps={{
              variant: "ghost",
              size: "sm",
              className: "w-full justify-start text-red hover:text-blue",
            }}
          />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderItemContent = (item, type) => {
    if (type === "lesson") {
      return (
        <span>
          <span className="font-body text-lg mr-2">{item.order}.</span>
          <span className="font-body text-base">{item.title}</span>
        </span>
      );
    }

    // For quizzes and projects, show title and additional info
    return (
      <div className="flex justify-between items-center w-full">
        <span className="font-body">{item.title}</span>
        <span className="font-body text-(--neutral-600) text-sm p-2">
          {type === "quiz" && `${item.score || 0} pts`}
          {type === "project" &&
            (item.score !== undefined ? `${item.score} pts` : "No points")}
        </span>
      </div>
    );
  };
  const contentSections = [
    {
      title: "Lessons",
      icon: <BookOpenIcon className="w-4 h-4" />,
      items: lessons,
      Dialog: AddLessonDialog,
      type: "lesson",
    },
    {
      title: "Quizzes",
      icon: <HelpCircle className="w-4 h-4" />,
      items: quizzes,
      Dialog: UnifiedQuizDialog,
      type: "quiz",
    },
    {
      title: "Projects",
      icon: <Code className="w-4 h-4" />,
      items: projects,
      Dialog: AddProjectDialog,
      type: "project",
    },
  ];

  const modifiedTopicName = topic.name.replace(/_/g, " ").trim();

  return (
    <Card
      key={topic.id}
      className="relative border border-(--neutral-300) text-(--neutral-900) mt-3"
    >
      <CardHeader className="flex justify-between items-start">
        <div>
         <CardTitle className="font-heading text-xl font-semibold text-(--neutral-900) ">
            {`${typeof topic.order === "number" ? topic.order.toFixed(1) : topic.order} ${modifiedTopicName}`}
          </CardTitle>

          <CardDescription className="text-sm font-body text-(--neutral-800)">
            {topic.description
              ? topic.description
              : "Topic description comes here"}
          </CardDescription>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="">
              <MoreVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="border border-(--neutral-300)">
            <DropdownMenuItem asChild>
              <EditTopicDialog
                topicId={topic.id}
                initialData={topic}
                triggerButtonProps={{
                  variant: "ghost",
                  size: "sm",
                  className: "w-full justify-start",
                }}
                refetchTopics={refetchTopics}
              />
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <DeleteTopic
                topic={topic}
                onDeleted={refetchTopics}
                triggerButtonProps={{
                  variant: "ghost",
                  size: "sm",
                  className: "w-full justify-start",
                }}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>

      <CardContent className="space-y-6">
        {contentSections.map(({ title, icon, items, Dialog, type }) => (
          <div key={title} className="space-y-2">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 text-(--neutral-800)">
                {icon}
                <h5 className="font-medium font-heading">{title}</h5>
              </div>
              <Dialog
                topicId={topic.id}
                topicOrder={topic.order}
                topicTitle={modifiedTopicName}
                onLessonAdded={
                  type === "lesson" ? handleLessonAdded : undefined
                }
                onQuizAdded={type === "quiz" ? handleQuizAdded : undefined}
                onProjectAdded={
                  type === "project" ? handleProjectAdded : undefined
                }
                nextOrder={(items ? items.length : 0) + 1}
                refetchTopics={refetchTopics}
                courseTitle={course?.title || "default-course"}
                courseSlug={course?.slug || "default-slug"}
              />
            </div>

            {items?.length > 0 ? (
              items.map((item) => (
                <div
                  key={`${type}-${item.id}`}
                  className="flex flex-row items-center border border-(--neutral-300)  rounded-md p-3 text-(--neutral-900)"
                >
                  <div className="flex-1 font-heading text-(--neutral-800)">
                    {renderItemContent(item, type)}
                  </div>

                  {/* Render different dropdown based on type */}
                  {type === "lesson" && renderLessonDropdown(item)}
                  {type === "quiz" && renderQuizDropdown(item)}
                  {type === "project" && renderProjectDropdown(item)}
                </div>
              ))
            ) : (
              <div className="border border-(--neutral-300) rounded-md p-3 bg-(--surface-panel)">
                <span className="text-sm font-body text-(--neutral-700)">
                  No {title.toLowerCase()} yet
                </span>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
